import { textureSmoke } from './smoke';
import { bossDeathAge } from './defeat';
import { authoredGeometry } from './authoredGeometry';
/**
 * Plan 15 P15.3 — the L2 boss as an authored TANK (research dossier §2): three rigid parts —
 * merged hull MESH `boss-torso` (the probe watch name + High-tier caster contract survive the
 * swap), merged turret MESH `boss-turret` on TransformNode `boss-turret-node`, merged barrel
 * MESH `boss-barrel` on `boss-barrel-node`. No skeleton (plan non-goal): all motion is PURE
 * f(snapshot) — strafe, hull rock, fire jolt, turret aim/freeze/ease, barrel recoil, death
 * droop — the `bossPose` discipline: a paused frame freezes exactly and a replay matches.
 *
 * Pinned to the sim contract (design §4):
 *  - the rest-pose rendered muzzle world z equals EXACTLY `boss.z + TANK_MUZZLE_DZ` (the
 *    authored node chain `turretNode.z + barrelNode.z + muzzleLocalZ` sums to that constant);
 *  - `bossFireAt < 0` (t = 0 / restart) ⇒ rest pose — no recoil, no jolt (the `bossHitAt` pattern);
 *  - turret yaw tracks `squadX` while silent, FREEZES on the locked band through telegraph and
 *    inFlight (the band never moves until detonation), and eases back to tracking over ~0.4 s
 *    post-detonation keyed on `slamAt` (no snap);
 *  - the shell is a RENDER-ONLY deterministic arc from the rendered muzzle at fire to
 *    (zone centre x, tankBandZ z) — `tankBandZ` is the one source the sim event, this telegraph
 *    quad, the tracer end and the FX burst all read.
 * The targeting telegraph is a NEW ground quad at `tankBandZ(spec)` (≈6 m reticle depth — NOT
 * the brute's standoff slab); only the brute's zone-scaling + sim-time-pulse pattern is borrowed.
 */
import {
  Color3,
  Constants,
  Matrix,
  Mesh,
  MeshBuilder,
  Quaternion,
  StandardMaterial,
  TransformNode,
  Vector3,
} from '@babylonjs/core/pure';
import type { PBRMaterial, Scene } from '@babylonjs/core/pure';
import type { RenderMaterials } from './scene';
import { BRIDGE_HALF_WIDTH } from './world';
import { applyBossGlow, toWorldX } from './actors';
import { BOSS_DEATH_HOLD } from './boss.skeletal';
import { bossStrafeX, tankBandZ, type SimState } from '../sim/sim';
import { TANK_ZONE_HALF_X, bossUsesFireCycle, type BossSpec, type LevelSpec } from '../sim/level';

/**
 * How long the dead tank stays on the bridge (s). The brute's hold, imported so the two bosses
 * share one number — the death read is stop + smoke plume + turret droop inside this window.
 */
export const TANK_DEATH_HOLD = BOSS_DEATH_HOLD;

/** Total triangle budget for the three merged parts (the actors.boss.test.ts pattern). */
export const TANK_TRI_BUDGET = 9000;

/**
 * Motion constants — every pose term below is built from these literals only. Amplitudes are
 * tuned to the strafe velocity scale: |d/dt bossStrafeX| peaks at
 * `strafeHalfWidth · 2π / strafePeriod ≈ 0.72 x-units/s` on L2.
 */
/** Authored-to-world scale for the tank (plan 15 fix round): the r1 geometry was authored at
 * ~half scale — a 1.7 m-wide tank beside 1.8 m soldiers read as a toy and the vision gate could
 * not find a "boss" at the standoff. ×1.45 gives genuine heavy-tank proportions (hull 2.47 m
 * wide — the visual half 1.23 m matches the sim hit interval hullHalfWidth 0.28 × 4.5 = 1.26 m,
 * "what you see is what hits you"; turret roof ≈ 2.6 m) while the strafing hull (max |x| 0.55 →
 * tread edge 2.02 m) stays inside the 4.5 m half-deck. The muzzle z chain −3.4 authored ×1.45
 * = −4.93 = TANK_MUZZLE_DZ exactly, so the shared sim constant keeps pinning the rendered tip. */
export const TANK_SCALE = 1.45;

export const TANK_MOTION = {
  /** Hull roll (lean into lateral motion), rad per x-unit/s of strafe velocity. */
  rollPerVel: 0.06,
  /** Hull pitch from |strafe velocity| (nose dips as it slides), rad per x-unit/s. */
  pitchPerVel: 0.05,
  /** Fire jolt: the front lifts once as the gun goes off, decaying exponentially. */
  joltPitch: 0.06,
  joltTau: 0.18,
  /** Barrel recoil stroke (m, backward = +z local) and decay constant (s). */
  recoilAmp: 0.42,
  recoilTau: 0.16,
  /** Post-detonation re-aim ease (s): the turret blends back to live tracking over this. */
  aimEaseS: 0.4,
  /** Muzzle-flash visibility window after bossFireAt (s). */
  maxFlashS: 0.12,
  /** Shell arc apex above the deck (m) — hit exactly at u = 0.5 (test-pinned). */
  arcApex: 4.5,
  /** Fade puffs trailing the tracer. */
  shellPuffs: 3,
  /** Death droop: turret pitches forward by this much over `droopS`. */
  droopRad: 0.28,
  droopS: 1.2,
  /** Safety clamp on the aim deflection (synthetic specs with a tiny standoff). */
  maxYaw: 0.65,
} as const;

/**
 * Authored rig numbers (metres, model faces −z toward the squad — the muzzle side). The chain
 * `turretNode.z + barrelNode.z + muzzleLocalZ = 0.35 − 0.9 − 2.85 = −3.4` is EXACTLY
 * `TANK_MUZZLE_DZ`, and `turretNode.y + barrelNode.y = muzzleY` is the muzzle height the FX
 * origin reads. Change one number, change the pinned rest pose — tank.test.ts holds the pin.
 */
export const TANK_PARTS = {
  turretNode: { y: 1.05, z: 0.35 },
  barrelNode: { y: 0.3, z: -0.9 },
  /** Muzzle tip in barrel-node space (the barrel centre-line sits at node y 0). */
  muzzleLocalZ: -2.85,
  /** Muzzle world height at rest (turret y + barrel y). */
  muzzleY: 1.35,
  /** Targeting reticle depth (m) — a tight band at the locked z, not the brute's 22 m slab. */
  telegraphDepth: 6,
  /** Telegraph pulse (the brute's pattern): alpha = a0 + a1·sin(sim.t·pulseHz·2π/18 …) — the
   *  brute's exact 0.22 + 0.18·sin(sim.t·18). */
  telegraphAlpha0: 0.22,
  telegraphAlpha1: 0.18,
  telegraphHz: 18,
  /** Death smoke plume: rise speed (m/s) and per-puff stagger (s). */
  plumeRise: 1.1,
  plumeStagger: 0.7,
} as const;

// ---------------------------------------------------------------------------
// Pure pose helpers — every one is a closed-form function of the snapshot. No dt, no state.
// ---------------------------------------------------------------------------

/**
 * d/dt of `bossStrafeX` (pure, render-local: the sim helper is position-only by design).
 *
 * Plan 19 D3: gated on `bossUsesFireCycle` (NOT `kind === 'tank'`) so it is the ONE strafe
 * derivative both fire-cycle bosses read — the heli banks/nose-dips on this same closed form
 * (heli.ts). Bit-identical for the tank: `kind === 'tank'` ⇒ the predicate is true, and a brute
 * spec (predicate false) keeps the exact 0 it returned before.
 */
export function bossStrafeVel(spec: BossSpec, t: number): number {
  if (!bossUsesFireCycle(spec) || !spec.strafeHalfWidth || !spec.strafePeriod) return 0;
  return (
    (spec.strafeHalfWidth * 2 * Math.PI) / spec.strafePeriod *
    Math.cos((2 * Math.PI * t) / spec.strafePeriod)
  );
}

/** Hull rock: roll leans into lateral motion, pitch follows |velocity|, plus the fire jolt.
 * GUARDED: `bossFireAt < 0` ⇒ no jolt (rest pose at t = 0 / restart). */
export function tankRock(
  spec: BossSpec,
  sim: SimState,
): { roll: number; pitch: number } {
  const v = bossStrafeVel(spec, sim.t);
  let pitch = TANK_MOTION.pitchPerVel * Math.abs(v);
  const roll = -TANK_MOTION.rollPerVel * v;
  if (sim.bossFireAt >= 0) {
    const age = sim.t - sim.bossFireAt;
    if (age >= 0 && age < 4 * TANK_MOTION.joltTau) {
      pitch += TANK_MOTION.joltPitch * Math.exp(-age / TANK_MOTION.joltTau);
    }
  }
  return { roll, pitch };
}

/** Barrel recoil stroke (m backward) from (t − bossFireAt), exponential decay.
 * GUARDED: `bossFireAt < 0` ⇒ 0 — the rest-pose guard, test-pinned. */
export function tankRecoil(sim: SimState): number {
  if (sim.bossFireAt < 0) return 0;
  const age = sim.t - sim.bossFireAt;
  if (!(age >= 0)) return 0; // restart rewind safety
  return TANK_MOTION.recoilAmp * Math.exp(-age / TANK_MOTION.recoilTau);
}

/**
 * Turret yaw (rotation.y). The barrel is authored facing −z, so aiming at a target x means
 * yaw = −atan2(dx, standoff): a squad to +x tips the gun toward +x (negative yaw).
 *
 *  - silent (no lock yet, or the ease long done): track `sim.squadX`;
 *  - telegraph / inFlight: FROZEN on the locked band centre (the band never moves — dodging
 *    reads as the gun holding its line while the squad escapes it);
 *  - silent right after a detonation (`slamAt` fresh): ease from the last band back to live
 *    tracking over ~0.4 s (smoothstep — continuous at both ends, no snap).
 */
export function tankAimYaw(spec: BossSpec, sim: SimState): number {
  const strafeX = bossStrafeX(spec, sim.t);
  const aimAt = (targetXu: number): number => {
    const yaw = -Math.atan2((targetXu - strafeX) * BRIDGE_HALF_WIDTH, spec.standoff);
    return Math.max(-TANK_MOTION.maxYaw, Math.min(TANK_MOTION.maxYaw, yaw));
  };
  const track = (): number => aimAt(sim.squadX);
  const band = (slam: NonNullable<SimState['slam']>): number => aimAt((slam.zone[0] + slam.zone[1]) / 2);
  const slam = sim.slam;
  if (!slam || sim.phase !== 'boss') return track();
  if (slam.active === 'telegraph' || slam.active === 'inFlight') return band(slam);
  // silent: `slam.zone` survives the cycle reset until the next lock, so it is the frozen ease
  // endpoint; `slamAt` is stamped exactly at the detonation.
  if (sim.slamAt !== null) {
    const u = (sim.t - sim.slamAt) / TANK_MOTION.aimEaseS;
    if (u >= 0 && u < 1) {
      const s = u * u * (3 - 2 * u);
      return band(slam) * (1 - s) + track() * s;
    }
  }
  return track();
}

/** A world-space point (the FX/tracer maths reads plain numbers, never Babylon objects). */
export interface TankPoint {
  x: number;
  y: number;
  z: number;
}

/**
 * The RENDERED muzzle world position for a snapshot — the same yaw/pitch/roll composition the
 * scene graph applies (Babylon Euler = RotationYawPitchRoll), so the muzzle flash and the shell
 * start sit exactly where the barrel tip renders. At rest (squadX = strafeX, bossFireAt −1) the
 * z component is exactly `spec.z + TANK_MUZZLE_DZ`.
 */
export function tankMuzzle(spec: BossSpec, sim: SimState): TankPoint {
  const yaw = tankAimYaw(spec, sim);
  const rock = tankRock(spec, sim);
  const barrel = new Vector3(0, TANK_PARTS.barrelNode.y * TANK_SCALE,
    (TANK_PARTS.barrelNode.z + tankRecoil(sim) + TANK_PARTS.muzzleLocalZ) * TANK_SCALE);
  const turret = Vector3.TransformCoordinates(barrel, Matrix.RotationY(yaw));
  turret.y += TANK_PARTS.turretNode.y * TANK_SCALE;
  turret.z += TANK_PARTS.turretNode.z * TANK_SCALE;
  const p = Vector3.TransformCoordinates(turret, Matrix.RotationYawPitchRoll(0, rock.pitch, rock.roll));
  return { x: toWorldX(bossStrafeX(spec, sim.t)) + p.x, y: p.y, z: spec.z + p.z };
}

/** Is the render-only shell alive for this snapshot? Bounded by phase: it dies at won/lost
 * (the sim never detonates a shell whose boss died — stepSim early-returns). */
export function shellAlive(spec: BossSpec, sim: SimState): boolean {
  const flight = spec.shellFlight ?? 0;
  if (sim.phase !== 'boss' || sim.bossFireAt < 0 || flight <= 0) return false;
  const age = sim.t - sim.bossFireAt;
  return age > 0 && age < flight;
}

/** The shell start: the rendered muzzle pose AS OF the fire moment — re-evaluated purely at
 * t = bossFireAt (strafe x, the frozen band yaw, full recoil at age 0). */
export function shellStart(spec: BossSpec, sim: SimState): TankPoint {
  const fireSim: SimState = { ...sim, t: sim.bossFireAt };
  return tankMuzzle(spec, fireSim);
}

/** The shell end: the locked band centre at `tankBandZ(spec)` — the one-source band z (the sim
 * event, this telegraph quad, the tracer end and the FX burst all read the same helper). */
export function shellEnd(spec: BossSpec, sim: SimState): TankPoint {
  const slam = sim.slam;
  const centre = slam ? (slam.zone[0] + slam.zone[1]) / 2 : sim.squadX;
  return { x: toWorldX(centre), y: 0.2, z: tankBandZ(spec) };
}

/**
 * The ONE ballistic tracer shape (plan 19 D3, plan-gate r1 copilot P2): lerp(start, end, u) plus a
 * parabolic lift chosen so the apex y at u = 0.5 is EXACTLY `apex`. Generic in its endpoints, so
 * the tank's main-gun shell and the heli's rocket share one arc (heli.ts feeds its own pod muzzle
 * and its own apex) — the maths is not duplicated, and `shellArcPoint` is exactly this call with
 * the tank's endpoints/constants. heli.test.ts pins the composition bit-for-bit.
 */
export function arcPoint(start: TankPoint, end: TankPoint, u: number, apex: number): TankPoint {
  const lift = apex - (start.y + end.y) / 2;
  return {
    x: start.x + (end.x - start.x) * u,
    y: start.y + (end.y - start.y) * u + 4 * lift * u * (1 - u),
    z: start.z + (end.z - start.z) * u,
  };
}

/**
 * Deterministic shell arc point at u ∈ [0, 1] — the shared `arcPoint` with the tank's rendered
 * muzzle/band endpoints and `TANK_MOTION.arcApex` (4.5 m, apex exact at u = 0.5).
 */
export function shellArcPoint(spec: BossSpec, sim: SimState, u: number): TankPoint {
  return arcPoint(shellStart(spec, sim), shellEnd(spec, sim), u, TANK_MOTION.arcApex);
}

// ---------------------------------------------------------------------------
// Authored geometry (dossier §2): low hull + sloped glacis + side skirts + two tread boxes +
// road wheels; boxy turret + mantlet + long barrel with muzzle brake + hatch + antenna.
// bossPlate-family atlas tiles + bossGlow slit emissives, one shared boss material.
// ---------------------------------------------------------------------------

/** Hull group, authored in ROOT space: names merged to `boss-torso` (probe watch + caster). */
function buildHull(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('tankHull', scene, material, 'boss-torso').mesh;
}

/** Turret group, authored in TURRET-NODE space; merged to `boss-turret`. */
function buildTurret(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('tankTurret', scene, material, 'boss-turret').mesh;
}

/** Barrel group, authored in BARREL-NODE space (centre-line at node y 0); merged to
 * `boss-barrel`. The tip sits at exactly `TANK_PARTS.muzzleLocalZ`. */
function buildBarrel(scene: Scene, material: PBRMaterial): Mesh {
  return authoredGeometry('tankBarrel', scene, material, 'boss-barrel').mesh;
}

// ---------------------------------------------------------------------------
// The actor.
// ---------------------------------------------------------------------------

export class TankBoss {
  readonly root: TransformNode;
  readonly turretNode: TransformNode;
  readonly barrelNode: TransformNode;
  readonly hull: Mesh;
  readonly turret: Mesh;
  readonly barrel: Mesh;
  readonly telegraph: Mesh;
  private readonly flash: Mesh;
  private readonly shell: Mesh;
  private readonly puffs: Mesh[] = [];
  private readonly plume: Mesh[] = [];
  private readonly material: PBRMaterial;
  private readonly flashMat: StandardMaterial;
  private readonly puffMat: StandardMaterial;
  private readonly plumeMat: StandardMaterial;

  constructor(scene: Scene, materials: RenderMaterials, private readonly spec: LevelSpec['boss']) {
    this.material = materials.boss as PBRMaterial;
    this.root = new TransformNode('boss-root', scene);
    this.root.position.set(0, 0, spec.z);

    this.hull = buildHull(scene, this.material);
    this.hull.parent = this.root;
    this.turretNode = new TransformNode('boss-turret-node', scene);
    this.turretNode.position.set(0, TANK_PARTS.turretNode.y * TANK_SCALE, TANK_PARTS.turretNode.z * TANK_SCALE);
    this.turretNode.parent = this.root;
    this.turret = buildTurret(scene, this.material);
    this.turret.parent = this.turretNode;
    this.barrelNode = new TransformNode('boss-barrel-node', scene);
    this.barrelNode.position.set(0, TANK_PARTS.barrelNode.y * TANK_SCALE, TANK_PARTS.barrelNode.z * TANK_SCALE);
    this.barrelNode.parent = this.turretNode;
    this.barrel = buildBarrel(scene, this.material);
    this.barrel.parent = this.barrelNode;

    // Targeting reticle at the locked band z (≈6 m deep), the brute's scaling/pulse pattern.
    this.telegraph = MeshBuilder.CreateGround(
      'tank-telegraph',
      { width: TANK_ZONE_HALF_X * 2 * BRIDGE_HALF_WIDTH, height: TANK_PARTS.telegraphDepth },
      scene,
    );
    this.telegraph.material = materials.glow(new Color3(1, 0.12, 0.05), TANK_PARTS.telegraphAlpha0 + TANK_PARTS.telegraphAlpha1);
    this.telegraph.position.y = 0.06;
    this.telegraph.isPickable = false;
    this.telegraph.setEnabled(false);

    this.flashMat = materials.glow(new Color3(2, 1.5, 0.7), 0.85);
    this.flash = MeshBuilder.CreatePlane('tank-muzzle-flash', { size: 1 }, scene);
    this.flash.material = this.flashMat;
    this.flash.isPickable = false;
    this.flash.setEnabled(false);

    const fx = (name: string, color: Color3): StandardMaterial => {
      const m = new StandardMaterial(name, scene);
      m.disableLighting = true;
      m.emissiveColor = color;
      m.alpha = 0;
      m.alphaMode = Constants.ALPHA_COMBINE; // smoke family: alpha blend, not additive
      m.backFaceCulling = false;
      m.disableDepthWrite = true;
      textureSmoke(m, scene);
      return m;
    };
    this.puffMat = fx('tank-shell-puff-mat', new Color3(0.75, 0.7, 0.62));
    this.shell = MeshBuilder.CreateCapsule(
      'tank-shell',
      { height: 0.55, radius: 0.07, tessellation: 6, capSubdivisions: 2 },
      scene,
    );
    this.shell.material = materials.glow(new Color3(2.6, 1.5, 0.6), 1);
    this.shell.isPickable = false;
    this.shell.setEnabled(false);
    for (let k = 0; k < TANK_MOTION.shellPuffs; k++) {
      const puff = MeshBuilder.CreatePlane(`tank-shell-puff-${k}`, { size: 1 }, scene);
      puff.material = this.puffMat;
      puff.isPickable = false;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.setEnabled(false);
      this.puffs.push(puff);
    }

    // Death plume (alpha-blended dark smoke — additive would vanish against the dark deck).
    this.plumeMat = fx('tank-plume-mat', new Color3(0.18, 0.17, 0.16));
    for (let k = 0; k < 3; k++) {
      const puff = MeshBuilder.CreatePlane(`tank-plume-${k}`, { size: 1 }, scene);
      puff.material = this.plumeMat;
      puff.isPickable = false;
      puff.billboardMode = Mesh.BILLBOARDMODE_ALL;
      puff.setEnabled(false);
      puff.parent = this.root;
      this.plume.push(puff);
    }
  }

  /**
   * Sync from the snapshot (pure — `dt` accepted for call-site parity, unused by contract).
   * Visible during 'boss' AND through the death-hold window after 'won' (stop + plume + droop).
   */
  update(sim: SimState, _dt: number, presentationAge?: number): void {
    const deathAge = bossDeathAge(sim, presentationAge);
    const corpse = deathAge !== null && deathAge >= 0 && deathAge <= TANK_DEATH_HOLD;
    const active = sim.phase === 'boss' || (sim.phase === 'won' && corpse);
    this.root.setEnabled(active);
    this.telegraph.setEnabled(false);
    this.flash.setEnabled(false);
    this.shell.setEnabled(false);
    for (const puff of this.puffs) puff.setEnabled(false);
    for (const puff of this.plume) puff.setEnabled(false);
    if (!active) return;

    // Root: strafe + hull rock. The strafe is f(sim.t), so it stops dead when the sim stops.
    const rock = tankRock(this.spec, sim);
    this.root.position.x = toWorldX(bossStrafeX(this.spec, sim.t));
    this.root.rotation.set(rock.pitch, 0, rock.roll);
    // Turret: aim/freeze/ease, plus the death droop (front dips as the fight is lost).
    this.turretNode.rotation.y = tankAimYaw(this.spec, sim);
    this.turretNode.rotation.x =
      sim.phase === 'won' && deathAge !== null
        ? -TANK_MOTION.droopRad * clamp01(deathAge / TANK_MOTION.droopS)
        : 0;
    // Barrel: recoil along the local z (backward = +z), GUARDED at bossFireAt < 0.
    this.barrelNode.position.z = (TANK_PARTS.barrelNode.z + tankRecoil(sim)) * TANK_SCALE;

    // Slit emissives ramp at the lock — the shared boss-material read (applyBossGlow's signature
    // fits the tank spec: slamPeriod/slamTelegraph are the same reused fire-cycle numbers).
    applyBossGlow(this.material, sim, this.spec);

    this.updateTelegraph(sim);
    this.updateFlash(sim);
    this.updateShell(sim);
    this.updatePlume(sim, deathAge);
  }

  /** Reticle: enabled through telegraph AND inFlight (the band is the danger read until the
   * shell detonates); zone-centre x, band z, width from the sim zone, sim-time pulse. */
  private updateTelegraph(sim: SimState): void {
    const slam = sim.slam;
    const show = sim.phase === 'boss' && !!slam && (slam.active === 'telegraph' || slam.active === 'inFlight');
    this.telegraph.setEnabled(show);
    if (!show || !slam) return;
    const [lo, hi] = slam.zone;
    this.telegraph.position.x = ((lo + hi) / 2) * BRIDGE_HALF_WIDTH;
    this.telegraph.position.z = tankBandZ(this.spec);
    const authored = TANK_ZONE_HALF_X * 2 * BRIDGE_HALF_WIDTH;
    this.telegraph.scaling.x = Math.max(0.2, ((hi - lo) * BRIDGE_HALF_WIDTH) / authored);
    (this.telegraph.material as StandardMaterial).alpha =
      TANK_PARTS.telegraphAlpha0 + TANK_PARTS.telegraphAlpha1 * Math.sin(sim.t * TANK_PARTS.telegraphHz);
  }

  /** Additive muzzle flash: f(fire age ≤ maxFlashS) at the rendered muzzle position. */
  private updateFlash(sim: SimState): void {
    const age = sim.bossFireAt >= 0 ? sim.t - sim.bossFireAt : -1;
    const show = sim.phase === 'boss' && age >= 0 && age <= TANK_MOTION.maxFlashS;
    this.flash.setEnabled(show);
    if (!show || age < 0) return;
    const m = tankMuzzle(this.spec, sim);
    this.flash.position.set(m.x, m.y + 0.12, m.z);
    const u = age / TANK_MOTION.maxFlashS;
    const s = 1.7 + 1.1 * u;
    this.flash.scaling.set(s, s, 1);
    this.flashMat.alpha = 0.85 * (1 - u) + 0.1;
  }

  /** Emissive tracer on the fixed arc + fading puff chain behind it — all f(age), bounded by
   * phase via `shellAlive`. */
  private updateShell(sim: SimState): void {
    const alive = shellAlive(this.spec, sim);
    this.shell.setEnabled(alive);
    const flight = this.spec.shellFlight ?? 0;
    const age = sim.t - Math.max(sim.bossFireAt, 0);
    const u = alive && flight > 0 ? age / flight : 0;
    if (alive) {
      const head = shellArcPoint(this.spec, sim, u);
      const ahead = shellArcPoint(this.spec, sim, Math.min(1, u + 0.02));
      const dx = ahead.x - head.x;
      const dy = ahead.y - head.y;
      const dz = ahead.z - head.z;
      const len = Math.hypot(dx, dy, dz) || 1;
      // Orient the +y capsule onto the arc tangent (the actors.ts orientAlong maths).
      const ax = dz / len;
      const az = -dx / len;
      const n = Math.hypot(ax, az);
      this.shell.rotationQuaternion =
        n < 1e-9
          ? Quaternion.Identity()
          : Quaternion.RotationAxis(new Vector3(ax / n, 0, az / n), Math.acos(dy / len));
      this.shell.position.set(head.x, head.y, head.z);
      this.puffMat.alpha = 0.4 * (1 - u);
      for (let k = 0; k < this.puffs.length; k++) {
        const uk = Math.max(0, u - (k + 1) * 0.09);
        const p = shellArcPoint(this.spec, sim, uk);
        this.puffs[k].setEnabled(true);
        this.puffs[k].position.set(p.x, p.y, p.z);
        const s = 0.35 + 0.25 * k;
        this.puffs[k].scaling.set(s, s, 1);
      }
    }
  }

  /** Death read: a pure f(deathAge) smoke plume over the corpse window. */
  private updatePlume(sim: SimState, deathAge: number | null): void {
    const show = sim.phase === 'won' && deathAge !== null && deathAge >= 0 && deathAge <= TANK_DEATH_HOLD;
    if (!show || deathAge === null) return;
    const fadeOut = 1 - clamp01((deathAge - (TANK_DEATH_HOLD - 1.2)) / 1.2);
    this.plumeMat.alpha = 0.5 * clamp01(deathAge / 0.4) * fadeOut;
    for (let k = 0; k < this.plume.length; k++) {
      const puff = this.plume[k]!;
      puff.setEnabled(true);
      puff.position.set(0.3 * Math.sin(deathAge * 1.7 + k * 2.1), 2.4 + k * TANK_PARTS.plumeStagger + deathAge * TANK_PARTS.plumeRise, 0.4);
      const s = 1.1 + k * 0.4 + deathAge * 0.9;
      puff.scaling.set(s, s, 1);
    }
  }
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
